import type { PriceDetailsQueryParams, PriceDetailsResponse } from './types';
import type { FiltersSelectionMapper } from '@/scopes/search/search/infrastructure/api/filtersSelectionMapper';
import { createFiltersSelectionMapper } from '@/scopes/search/search/infrastructure/api/filtersSelectionMapper';
import type { QueryStringSerializer } from '@/scopes/common/infrastructure/query-strings/queryStringSerializer';
import { createQueryStringSerializer } from '@/scopes/common/infrastructure/query-strings/queryStringSerializer';
import type { SearchFilters } from '@/scopes/search/search/infrastructure/api/types';
import defaultFetch from 'isomorphic-unfetch';

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string;
  };
  fetch: typeof defaultFetch;
  queryStringSerializer: QueryStringSerializer;
  filterSelectionMapper: FiltersSelectionMapper<SearchFilters>;
}

export const createPriceDetailsApiClient = (
  {
    contextItemId,
  }: {
    contextItemId: string;
  },
  dependencies: Partial<Dependencies> = {}
) => {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
    queryStringSerializer = createQueryStringSerializer(),
    filterSelectionMapper = createFiltersSelectionMapper<SearchFilters>(),
  } = dependencies as Dependencies;

  const fetchOptions: RequestInit = {
    referrerPolicy: 'no-referrer-when-downgrade',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  };

  async function getPriceDetails(priceDetailsQueryParams: PriceDetailsQueryParams): Promise<PriceDetailsResponse> {
    const url = buildAPIUrl(priceDetailsQueryParams);

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      throw new Error('Failed to fetch price details');
    }

    return (await response.json()) as PriceDetailsResponse;
  }

  function buildAPIUrl(priceDetailsQueryParams: PriceDetailsQueryParams) {
    const { transportType, accommodationId, duration, mealPlan, departureDate, participants } = priceDetailsQueryParams;

    const mappedParticipants = filterSelectionMapper.map({ Participants: participants });
    const queryString = queryStringSerializer.serialize({
      transportType,
      accommodationId,
      duration,
      mealPlan,
      departureDate,
      contextItemId,
      ...mappedParticipants,
    });
    return `${env.NEXT_PUBLIC_API_BASE_URL}/PriceDetails/GetPriceDetailsApi?${queryString}`;
  }

  return { getPriceDetails };
};
